import { useQuery } from "@tanstack/react-query";
import { useSeatLocationApi } from "./SeatLocationApiContext.js";
import { parseSeatFloorPlan, parseMemberSeatLocation } from "./seatLocation.js";
const seatLocationKeys = {
  all: ["seating", "location"],
  member: (memberGuid) => [...seatLocationKeys.all, "member", memberGuid],
  floor: (floorId) => [...seatLocationKeys.all, "floor", floorId]
};
const PLAN_STALE_TIME = 5 * 60 * 1e3;
const isSeatUnavailable = (error) => {
  var _a;
  const status = (_a = error == null ? void 0 : error.response) == null ? void 0 : _a.status;
  return status === 404 || status === 401 || status === 403;
};
const useSeatLocationAPIs = () => {
  const api = useSeatLocationApi();
  const requireApi = () => {
    if (!api) {
      throw new Error("좌석 조회가 꺼져 있습니다 (SeatLocationApiProvider).");
    }
    return api;
  };
  const getMemberSeat = (memberGuid) => {
    const { client, basePath } = requireApi();
    return client.get(
      `${basePath}/pins/by-employee/${encodeURIComponent(memberGuid)}`
    ).then(parseMemberSeatLocation);
  };
  const getFloorPlan = (floorId) => {
    const { client, basePath } = requireApi();
    return client.get(`${basePath}/floors/${encodeURIComponent(floorId)}`).then(parseSeatFloorPlan);
  };
  return { isEnabled: !!api, getMemberSeat, getFloorPlan };
};
const useFetchMemberSeat = (memberGuid) => {
  const { isEnabled, getMemberSeat } = useSeatLocationAPIs();
  return useQuery({
    queryKey: seatLocationKeys.member(memberGuid ?? ""),
    queryFn: async () => {
      try {
        return await getMemberSeat(memberGuid);
      } catch (error) {
        if (isSeatUnavailable(error)) return null;
        throw error;
      }
    },
    enabled: isEnabled && !!memberGuid,
    staleTime: PLAN_STALE_TIME
  });
};
const useFetchSeatFloorPlan = (floorId) => {
  const { isEnabled, getFloorPlan } = useSeatLocationAPIs();
  return useQuery({
    queryKey: seatLocationKeys.floor(floorId ?? ""),
    queryFn: () => getFloorPlan(floorId),
    enabled: isEnabled && !!floorId,
    staleTime: PLAN_STALE_TIME
  });
};
export {
  seatLocationKeys,
  useFetchMemberSeat,
  useFetchSeatFloorPlan,
  useSeatLocationAPIs
};
